import React from "react";
import { toast } from "sonner";
import {
  Bell, BellOff, Loader2, LogOut, MoreHorizontal, Paperclip, Pencil, Send, Trash2,
  UserPlus, Users, X,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { ChatMessageText } from "@/components/social/ChatMessageText";
import { MessageAvatar } from "@/components/social/MessageAvatar";
import { AuthorLink, AuthorAvatar } from "@/components/social/AuthorLink";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { ReactionBar } from "@/components/social/ReactionBar";
import { ReportDialog } from "@/components/social/ReportDialog";
import { GroupNewMessageDialog } from "@/components/groups/GroupNewMessageDialog";
import { applyReactionToggle } from "@/lib/reactions";
import { fullTime, messageStamp } from "@/lib/timeago";
import { cn } from "@/lib/utils";

const SYSTEM_TEXT = {
  conversation_created: "started this conversation",
  participant_added: "added a participant",
  participant_left: "left the conversation",
  participant_removed: "removed a participant",
  conversation_renamed: "renamed the conversation",
  manager_transferred: "transferred the conversation manager",
};

function SystemEvent({ msg, names }) {
  const code = msg.event?.code;
  const who = names[msg.sender_id] || "A member";
  const about = msg.event?.user_id ? names[msg.event.user_id] : null;
  const label = code === "conversation_renamed" && msg.event?.name
    ? `renamed the conversation to “${msg.event.name}”`
    : SYSTEM_TEXT[code] || "updated the conversation";
  return (
    <div className="flex justify-center py-1" data-testid={`group-message-system-${msg.id}`}>
      <span className="rounded-full bg-secondary/70 px-3 py-1 text-xs text-muted-foreground">
        {who} {label}{about ? ` — ${about}` : ""}
      </span>
    </div>
  );
}

/** A manager-only participant sheet: add, remove, transfer (spec §8). */
function ParticipantsDialog({ groupId, thread, open, onOpenChange, onChanged }) {
  const [adding, setAdding] = React.useState(false);
  const [confirmAdd, setConfirmAdd] = React.useState(null);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work."); }
  };
  const base = `/groups/${groupId}/messages/threads/${thread.id}`;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="group-thread-participants-dialog">
          <DialogHeader>
            <DialogTitle>
              Participants · {thread.participant_count} of {thread.max_participants}
            </DialogTitle>
          </DialogHeader>
          <div className="divide-y rounded-lg border">
            {(thread.participants || []).map((p) => (
              <div key={p.user_id} className="flex items-center gap-3 px-3 py-2"
                   data-testid={`group-thread-participant-${p.user_id}`}>
                <AuthorAvatar card={p}>
                  <Avatar className="h-8 w-8">
                    {p.headshot_url ? <AvatarImage src={fileUrl(p.headshot_url)} alt="" /> : null}
                    <AvatarFallback className="text-[11px]">
                      {(p.display_name || "?")[0]}
                    </AvatarFallback>
                  </Avatar>
                </AuthorAvatar>
                <span className="min-w-0 flex-1">
                  <AuthorLink card={p}
                              className="block truncate text-sm font-[560] hover:text-primary"
                              testId={`group-thread-participant-link-${p.user_id}`} />
                  <span className="block truncate text-xs text-muted-foreground">
                    {p.active ? (p.role === "manager" ? "Conversation manager" : "Participant")
                      : "No longer in this conversation"}
                    {p.active && !p.in_group ? " · no longer in the group" : ""}
                  </span>
                </span>
                {thread.is_manager && p.active && p.role !== "manager" ? (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="outline"
                            data-testid={`group-thread-make-manager-${p.user_id}`}
                            onClick={() => act(() => api.post(`${base}/manager`,
                              { user_id: p.user_id }), "Conversation manager transferred.")}>
                      Make manager
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive"
                            data-testid={`group-thread-remove-${p.user_id}`}
                            onClick={() => act(() => api.delete(
                              `${base}/participants/${p.user_id}`), "Participant removed.")}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          {thread.is_manager ? (
            <Button variant="outline" onClick={() => setAdding(true)}
                    data-testid="group-thread-add-participant">
              <UserPlus className="mr-2 h-4 w-4" />Add people
            </Button>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* The history warning is shown BEFORE the add is confirmed (spec §10). */}
      <GroupNewMessageDialog groupId={groupId} open={adding} onOpenChange={setAdding}
                             maxParticipants={thread.max_participants}
                             onCreated={() => {}}
                             /* Re-used picker, but the action is "add", so it hands the
                                selection back for confirmation instead of creating. */
                             addMode onPick={(ids) => setConfirmAdd(ids)} />
      <Dialog open={Boolean(confirmAdd)} onOpenChange={() => setConfirmAdd(null)}>
        <DialogContent data-testid="group-thread-history-warning">
          <DialogHeader>
            <DialogTitle>Add to this conversation?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/80">
            New participants will be able to see this conversation&apos;s previous messages.
          </p>
          <Button onClick={() => act(async () => {
            await api.post(`${base}/participants`, { user_ids: confirmAdd });
            setConfirmAdd(null);
          }, "Added to the conversation.")} data-testid="group-thread-confirm-add">
            Add and share history
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Group Member Messaging V1 — the conversation that REPLACES the Stream feed in the centre
 * of the Group workspace (spec §15). The member never leaves the Group shell and is never
 * sent into the platform-wide DM experience.
 *
 * Presentation is deliberately the canonical Chatter/ChatDock vocabulary (bubbles, the
 * shared `ReactionBar`, the shared emoji picker, `ChatMessageText` autolinking, the shared
 * report dialog) while the STORAGE and authorization are the private messaging model —
 * nothing here writes a Chatter post.
 */
export function GroupMessageThread({ group, threadId, onClose, onChanged }) {
  const { user } = useAuth();
  const me = user?.user_id;
  const base = `/groups/${group.id}/messages/threads/${threadId}`;
  const [thread, setThread] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [editText, setEditText] = React.useState("");
  const [people, setPeople] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");
  const [reporting, setReporting] = React.useState(null);
  const [pending, setPending] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  const names = React.useMemo(() => Object.fromEntries(
    (thread?.participants || []).map((p) => [p.user_id, p.display_name])), [thread]);
  // The participant rows ARE profile cards (display name, slug, headshot), so the avatar
  // beside a message and the link on a name come from the same hydration the header uses
  // — no extra request per message.
  const cards = React.useMemo(() => Object.fromEntries(
    (thread?.participants || []).map((p) => [p.user_id, p])), [thread]);

  const loadThread = React.useCallback(async () => {
    const { data } = await api.get(base);
    setThread(data);
    setNameDraft(data.name || "");
  }, [base]);

  const loadMessages = React.useCallback(async () => {
    const { data } = await api.get(`${base}/messages`, { params: { limit: 30 } });
    setMessages(data.messages || []);
    setHasMore(Boolean(data.has_more));
  }, [base]);

  React.useEffect(() => {
    let live = true;
    setLoading(true); setMessages([]); setThread(null);
    Promise.all([loadThread(), loadMessages()])
      .then(() => { if (live) api.post(`${base}/read`, {}).then(onChanged).catch(() => {}); })
      .catch(() => { if (live) toast.error("That conversation isn't available."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  // A new message arrives without a reload and without dragging the reader away from
  // older history they are reading: the newest page is re-read and the cursor advanced.
  useRealtimeEvent((evt) => {
    if (evt.type !== "group_message" || evt.data?.thread_id !== threadId) return;
    loadMessages().then(() => api.post(`${base}/read`, {}).then(onChanged)).catch(() => {});
  });
  useRealtimeEvent((evt) => {
    if (evt.type !== "group_message_reaction" || evt.data?.thread_id !== threadId) return;
    loadMessages().catch(() => {});
  });

  React.useEffect(() => {
    if (!loading && bottomRef.current) bottomRef.current.scrollIntoView({ block: "nearest" });
  }, [loading, messages.length]);

  const loadOlder = async () => {
    if (!messages.length) return;
    setLoadingOlder(true);
    try {
      const { data } = await api.get(`${base}/messages`,
        { params: { limit: 30, before: messages[0].created_at } });
      setMessages((prev) => [...(data.messages || []), ...prev]);
      setHasMore(Boolean(data.has_more));
    } finally { setLoadingOlder(false); }
  };

  const send = async () => {
    const body = text.trim();
    if (!body && pending.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post(`${base}/messages`, { body, media: pending });
      setMessages((prev) => [...prev, data]);
      setText(""); setPending([]);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that message.");
    } finally { setSending(false); }
  };

  const attach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "group_message");
      const { data } = await api.post("/files/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPending((prev) => [...prev, { url: data.url, content_type: data.content_type,
        name: file.name, size: data.size }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach that file.");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const react = async (messageId, key) => {
    setMessages((prev) => prev.map((msg) => (msg.id === messageId
      ? applyReactionToggle(msg, key) : msg)));
    try { await api.post(`${base}/messages/${messageId}/react`, { emoji: key }); }
    catch (e) { loadMessages().catch(() => {}); }
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`${base}/messages/${editing}`, { body: editText });
      setMessages((prev) => prev.map((msg) => (msg.id === editing ? data : msg)));
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that edit."); }
  };

  const remove = async (messageId) => {
    try {
      const { data } = await api.delete(`${base}/messages/${messageId}`);
      setMessages((prev) => prev.map((msg) => (msg.id === messageId ? data : msg)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete that."); }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await loadThread(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work."); }
  };

  if (loading || !thread) {
    return (
      <Card className="flex items-center justify-center border-0 p-10"
            data-testid="group-thread-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  const multi = thread.type === "multi";
  return (
    <div className="min-w-0 space-y-3" data-testid="group-message-thread">
      <Card className="flex flex-wrap items-center gap-3 border-0 p-4">
        {!multi && thread.other ? (
          <AuthorAvatar card={thread.other}>
            <Avatar className="h-10 w-10">
              {thread.other.headshot_url
                ? <AvatarImage src={fileUrl(thread.other.headshot_url)} alt="" /> : null}
              <AvatarFallback>{(thread.other.display_name || "?")[0]}</AvatarFallback>
            </Avatar>
          </AuthorAvatar>
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
            <Users className="h-5 w-5 text-muted-foreground" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold" data-testid="group-thread-title">
            {/* In a 1:1 the heading IS a person, so it links to their profile. */}
            {!multi && thread.other ? (
              <AuthorLink card={thread.other} className="hover:text-primary"
                          testId="group-thread-title-link" />
            ) : thread.label}
          </h2>
          <p className="truncate text-sm text-muted-foreground"
             data-testid="group-thread-subtitle">
            {multi
              ? `${thread.participant_count} members · ${thread.group_name}`
              : `Member of ${thread.group_name}`}
          </p>
        </div>
        {thread.muted ? (
          <Badge variant="secondary" className="gap-1" data-testid="group-thread-muted-badge">
            <BellOff className="h-3 w-3" />Muted
          </Badge>
        ) : null}
        {multi ? (
          <Button size="sm" variant="outline" onClick={() => setPeople(true)}
                  data-testid="group-thread-participants">
            <Users className="mr-1.5 h-3.5 w-3.5" />{thread.participant_count}
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" aria-label="Conversation options"
                    data-testid="group-thread-menu">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem data-testid="group-thread-mute"
                              onClick={() => act(() => api.post(`${base}/mute`,
                                { muted: !thread.muted }),
                              thread.muted ? "Conversation unmuted." : "Conversation muted.")}>
              {thread.muted ? <Bell className="mr-2 h-4 w-4" />
                : <BellOff className="mr-2 h-4 w-4" />}
              {thread.muted ? "Unmute conversation" : "Mute conversation"}
            </DropdownMenuItem>
            {multi && thread.is_manager ? (
              <DropdownMenuItem onClick={() => setRenaming(true)}
                                data-testid="group-thread-rename">
                <Pencil className="mr-2 h-4 w-4" />Rename conversation
              </DropdownMenuItem>
            ) : null}
            {multi && thread.active ? (
              <DropdownMenuItem className="text-destructive focus:text-destructive"
                                data-testid="group-thread-leave"
                                onClick={() => act(() => api.post(`${base}/leave`, {}),
                                  "You left the conversation.")}>
                <LogOut className="mr-2 h-4 w-4" />Leave conversation
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="icon" variant="ghost" onClick={onClose} aria-label="Close conversation"
                data-testid="group-thread-close">
          <X className="h-5 w-5" />
        </Button>
      </Card>

      <Card className="border-0 p-3">
        {hasMore ? (
          <div className="flex justify-center pb-2">
            <Button size="sm" variant="ghost" onClick={loadOlder} disabled={loadingOlder}
                    data-testid="group-thread-load-older">
              {loadingOlder ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Load earlier messages
            </Button>
          </div>
        ) : null}
        <div className="space-y-2">
          {messages.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground"
               data-testid="group-thread-empty">
              No messages yet — say hello.
            </p>
          ) : null}
          {messages.map((msg) => {
            if (msg.kind === "system") {
              return <SystemEvent key={msg.id} msg={msg} names={names} />;
            }
            const mine = msg.sender_id === me;
            const sender = cards[msg.sender_id];
            return (
              <div key={msg.id} className={cn("group flex items-start gap-2",
                mine ? "flex-row-reverse" : "flex-row")}
                   data-testid={`group-message-${msg.id}`}>
                <MessageAvatar card={sender} mine={mine} size="h-8 w-8"
                               testId={`group-message-avatar-${msg.id}`} />
                <div className={cn("flex min-w-0 flex-col",
                  mine ? "items-end" : "items-start")}>
                {!mine ? (
                  <AuthorLink card={sender}
                              className="px-1 text-[11px] font-[560] text-muted-foreground hover:text-primary"
                              testId={`group-message-sender-${msg.id}`}
                              fallback={names[msg.sender_id] || "Member"} />
                ) : null}
                {editing === msg.id ? (
                  <div className="flex w-full max-w-[80%] items-center gap-1">
                    <Input value={editText} onChange={(e) => setEditText(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                           data-testid={`group-message-edit-input-${msg.id}`} />
                    <Button size="sm" onClick={saveEdit}
                            data-testid={`group-message-edit-save-${msg.id}`}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className={cn("max-w-[80%] rounded-2xl px-3 py-1.5 text-sm leading-5",
                    msg.deleted ? "bg-secondary/50 italic text-muted-foreground"
                      : mine ? "rounded-br-sm bg-primary text-primary-foreground"
                        : "rounded-bl-sm bg-secondary text-foreground")}
                       title={fullTime(msg.created_at)}>
                    {msg.deleted ? "Message deleted"
                      : <ChatMessageText body={msg.body} />}
                    {(msg.media || []).map((item) => (
                      <a key={item.url} href={fileUrl(item.url)} target="_blank"
                         rel="noopener noreferrer"
                         className="mt-1 flex items-center gap-1 underline underline-offset-2"
                         data-testid={`group-message-attachment-${msg.id}`}>
                        <Paperclip className="h-3 w-3" />{item.name || "Attachment"}
                      </a>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  {!msg.deleted ? (
                    <ReactionBar reactions={msg.reactions} myReaction={msg.my_reaction}
                                 onReact={(k) => react(msg.id, k)}
                                 testId={`group-message-react-${msg.id}`} />
                  ) : null}
                  <span className="px-1 text-[10px] text-muted-foreground">
                    {messageStamp(msg.created_at, user?.timezone)}
                    {msg.edited_at ? (
                      <span data-testid={`group-message-edited-${msg.id}`}> · Edited</span>
                    ) : null}
                  </span>
                  {!msg.deleted ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-6 w-6"
                                aria-label="Message options"
                                data-testid={`group-message-menu-${msg.id}`}>
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align={mine ? "end" : "start"} className="w-40">
                        {mine && thread.can_send ? (
                          <DropdownMenuItem
                            onClick={() => { setEditing(msg.id); setEditText(msg.body); }}
                            data-testid={`group-message-edit-${msg.id}`}>
                            <Pencil className="mr-2 h-4 w-4" />Edit
                          </DropdownMenuItem>
                        ) : null}
                        {mine ? (
                          <DropdownMenuItem className="text-destructive focus:text-destructive"
                                            onClick={() => remove(msg.id)}
                                            data-testid={`group-message-delete-${msg.id}`}>
                            <Trash2 className="mr-2 h-4 w-4" />Delete
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem className="text-destructive focus:text-destructive"
                                            onClick={() => setReporting(msg.id)}
                                            data-testid={`group-message-report-${msg.id}`}>
                            Report message
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {thread.can_send ? (
          <div className="mt-3 border-t pt-3">
            {pending.length ? (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="group-thread-pending">
                {pending.map((item) => (
                  <Badge key={item.url} variant="secondary" className="gap-1 pr-1">
                    <Paperclip className="h-3 w-3" />{item.name}
                    <button type="button" aria-label={`Remove ${item.name}`}
                            onClick={() => setPending((prev) =>
                              prev.filter((x) => x.url !== item.url))}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
            <div className="flex items-center gap-1">
              <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText}
                                 testId="group-thread-emoji" />
              <input ref={fileRef} type="file" className="hidden"
                     onChange={(e) => attach(e.target.files?.[0])}
                     data-testid="group-thread-file-input" />
              <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0"
                      disabled={uploading} aria-label="Attach a file"
                      onClick={() => fileRef.current?.click()}
                      data-testid="group-thread-attach">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Paperclip className="h-4 w-4" />}
              </Button>
              <Input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                     placeholder={`Message ${thread.label}…`} className="h-9"
                     aria-label="Write a message"
                     data-testid="group-thread-input" />
              <Button size="icon" className="h-9 w-9 shrink-0" onClick={send}
                      disabled={sending || (!text.trim() && pending.length === 0)}
                      aria-label="Send message" data-testid="group-thread-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-3 border-t pt-3 text-center text-sm text-muted-foreground"
             data-testid="group-thread-readonly">
            {thread.why_text || "This conversation is read-only."}
          </p>
        )}
      </Card>

      {multi ? (
        <ParticipantsDialog groupId={group.id} thread={thread} open={people}
                            onOpenChange={setPeople}
                            onChanged={() => { loadThread(); loadMessages(); onChanged(); }} />
      ) : null}

      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent data-testid="group-thread-rename-dialog">
          <DialogHeader><DialogTitle>Rename conversation</DialogTitle></DialogHeader>
          <Input value={nameDraft} maxLength={80}
                 onChange={(e) => setNameDraft(e.target.value)}
                 data-testid="group-thread-rename-input" />
          <Button data-testid="group-thread-rename-save"
                  onClick={() => act(async () => {
                    await api.post(`${base}/rename`, { name: nameDraft });
                    setRenaming(false);
                  }, "Conversation renamed.")}>
            Save name
          </Button>
        </DialogContent>
      </Dialog>

      <ReportDialog open={Boolean(reporting)} onOpenChange={() => setReporting(null)}
                    contentType="group_message" contentId={reporting} />
    </div>
  );
}
